import { getReduxStore } from '@Common/redux/store';
import { APP_SETTINGS } from 'config/constants';
import { spamCheck, solveRecaptcha } from '@Common/handlers/api';
import { setSpamCheckCallStatus, toggleRecaptchaAction } from '@Common/actions/commonActions';
import { Queue } from 'core/models/queue';
import { openToastr } from 'src/_common/handlers/services';
import Bugsnag from '@bugsnag/js';
import logger from '../logger/logger';

class Recaptcha {
  private static instance: Recaptcha | null;

  private static isInitiated = false;

  private static refreshTimer: ReturnType<typeof setTimeout> | null;

  private static isRunning: boolean;

  private static cbQueue: Queue<Function>;

  private static recaptchaTrials = 0;

  private static listenForRecaptchaEvent = false;

  private constructor() {
    const grecaptchaElement = document.createElement('script');
    grecaptchaElement.type = 'application/javascript';
    grecaptchaElement.src =
      'https://www.google.com/recaptcha/api.js?render=6LdNFngUAAAAAPYlMOGQOclmFYl6buFVajKnmFBU';
    document.body.appendChild(grecaptchaElement);

    Recaptcha.isRunning = false;
    Recaptcha.cbQueue = new Queue();
  }

  public static getInstance() {
    if (!Recaptcha.instance) Recaptcha.instance = new Recaptcha();

    return Recaptcha.instance;
  }

  public destroy() {
    Recaptcha.instance = null;
    if (Recaptcha.refreshTimer) clearInterval(Recaptcha.refreshTimer);
    Recaptcha.refreshTimer = null;
    Recaptcha.listenForRecaptchaEvent = false;
    Recaptcha.isInitiated = false;
    Recaptcha.isRunning = false;
    Recaptcha.cbQueue.destroy();
  }

  public recaptchaEventEmitter(cb: Function, shouldSolveRecaptcha = true, sendToken?: boolean) {
    if (Recaptcha.listenForRecaptchaEvent) {
      const data = { cb, shouldSolveRecaptcha };
      // skips v3 implementation
      if (data.shouldSolveRecaptcha === false) {
        getReduxStore()?.dispatch(
          toggleRecaptchaAction({
            isShown: true,
          })
        );
        this.renderElement(data.cb, data.shouldSolveRecaptcha);
        return;
      }
      Recaptcha.cbQueue.enqueue(data.cb);

      logger(
        '[CBQUEUE] ADDED CB',
        `Total Number of pending tasks now are ${Recaptcha.cbQueue.length()}`
      );
      if (!Recaptcha.isRunning) {
        Recaptcha.isRunning = true;
        Recaptcha.getInstance().initGoogleRecaptcha(
          Recaptcha.getInstance().cbQueueExecutor,
          sendToken
        );

        logger(
          '[CBQUEUE] INITIATED CB',
          `Total Number of pending tasks now are ${Recaptcha.cbQueue.length()}`
        );
      }
    }
  }

  public init() {
    if (!Recaptcha.isInitiated) {
      Recaptcha.isInitiated = true;

      Recaptcha.listenForRecaptchaEvent = true;

      // This has been handled in the server. DO NOT REMOVE in case needed in next release
      // QuillEventEmitter.getInstance().on(
      //   'RENDER_RECAPTCHA_PUZZLE_EVENT',
      //   (data: { cb: Function }) => {
      //     Recaptcha.cbQueue.enqueue(data.cb);

      //     logger(
      //       '[CBQUEUE] ADDED CB',
      //       `Total Number of pending tasks now are ${Recaptcha.cbQueue.length()}`
      //     );

      //     if (!Recaptcha.isRunning) {
      //       Recaptcha.getInstance().renderElement(Recaptcha.getInstance().cbQueueExecutor);
      //       Recaptcha.isRunning = true;

      //       logger(
      //         '[CBQUEUE] INITIATED CB',
      //         `Total Number of pending tasks now are ${Recaptcha.cbQueue.length()}`
      //       );
      //     }
      //   }
      // );
      // Recaptcha.getInstance().startRecaptchaTimer();
      Recaptcha.getInstance().initGoogleRecaptcha();
    }
  }

  cbQueueExecutor() {
    while (!Recaptcha.cbQueue.isEmpty()) {
      const cb = Recaptcha.cbQueue.dequeue();
      if (cb) cb();
    }
    Recaptcha.isRunning = false;
  }

  initGoogleRecaptcha(cb?: Function, sendToken?: boolean) {
    if (getReduxStore()?.getState()?.user?.premium) {
      if (cb && typeof cb === 'function') cb();
      return;
    }
    if (!sendToken) {
      spamCheck().then((botLevl) => {
        Recaptcha.getInstance().handleBotLevel(botLevl as { score: number }, cb);
        getReduxStore()?.dispatch(setSpamCheckCallStatus(true));
      });
      return;
    }
    if (typeof window !== 'undefined' && window.grecaptcha && window.grecaptcha.execute) {
      window.grecaptcha
        .execute('6LdNFngUAAAAAPYlMOGQOclmFYl6buFVajKnmFBU', {
          action: 'visit_site',
        })
        .then(
          (token: string) => {
            spamCheck(token).then((botLevl) => {
              Recaptcha.getInstance().handleBotLevel(botLevl as { score: number }, cb);
              getReduxStore()?.dispatch(setSpamCheckCallStatus(true));
            });
          },
          (e: unknown) => logger('Google Recaptcha Error', e)
        );
    } else {
      setTimeout(() => {
        if (
          process.browser &&
          document.readyState === 'complete' &&
          Recaptcha.recaptchaTrials < 5
        ) {
          Recaptcha.getInstance().initGoogleRecaptcha(cb);
          Recaptcha.recaptchaTrials += 1;
        } else if (
          process.browser &&
          document.readyState === 'complete' &&
          Recaptcha.recaptchaTrials > 5
        ) {
          openToastr('Your browser settings is blocking recaptcha');
          Bugsnag.notify({
            name: 'RECAPTCHA_LOAD_ERROR',
            message: 'Recaptcha not loaded for user',
          });
        } else Recaptcha.getInstance().initGoogleRecaptcha(cb);
      }, 100);
    }
  }

  private startRecaptchaTimer() {
    if (getReduxStore()?.getState()?.user?.premium) return;
    // Don't give a function call back to initGoogleRecaptcha call here. This is just a silent check. Won't show Recpatcha puzzle if it fails.
    Recaptcha.refreshTimer = setInterval(() => this.initGoogleRecaptcha(), 1000 * 60 * 30);
  }

  private handleBotLevel(botLvl: { score: number }, cb?: Function) {
    if (
      botLvl.score >= APP_SETTINGS.BOT_VERIFY_THRESH ||
      getReduxStore()?.getState()?.user?.premium
    ) {
      if (cb && typeof cb === 'function') cb();
      return;
    }
    // Proceeding only when the we have a calllback. If no callback, then it is the initial call. Doing nothing in this case.
    // When user will try to quill something, 408 will come up and this will be executed.
    if (typeof cb === 'function') {
      getReduxStore()?.dispatch(
        toggleRecaptchaAction({
          isShown: true,
        })
      );
      Recaptcha.getInstance().renderElement(cb);
    }
  }

  public renderElement(cb: Function, shouldSolveRecaptcha = true) {
    if (getReduxStore()?.getState()?.user?.premium) {
      if (cb && typeof cb === 'function') cb();
      return;
    }

    const el = document.getElementById('quill-recaptcha-container');
    if (!el) {
      setTimeout(() => Recaptcha.getInstance().renderElement(cb), 500);
      logger('[MIS] Recaptcha', 'Missing Recaptcha Element');
      return;
    }
    try {
      window.grecaptcha.render('quill-recaptcha-container', {
        sitekey: '6LelrTMUAAAAADFNWeD4L_GTnuyWu0i0066dweLS',
        theme: getReduxStore()?.getState()?.common?.theme || 'light',
        callback: (token: string) => {
          const closeRecaptcha = () =>
            getReduxStore()?.dispatch(
              toggleRecaptchaAction({
                isShown: false,
              })
            );

          if (shouldSolveRecaptcha) {
            solveRecaptcha({ token }).then((res) => {
              if (res.status === 'success') {
                closeRecaptcha();
                if (cb && typeof cb === 'function') cb();
              }
            });
          } else if (cb && typeof cb === 'function' && !shouldSolveRecaptcha) {
            closeRecaptcha();
            cb(token);
          }
        },
      });
    } catch (e) {
      logger('[DUB] Recaptcha', 'Dublicate Recaptcha Instance');
    }
  }
}

export default Recaptcha;
